import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { usePublicMode } from "./hooks/usePublicMode";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { CartDrawer } from "./components/CartDrawer";
import { GraceAssistant } from "./components/GraceAssistant";
import {
  SplashCurtain,
  shouldShowSplash,
  markSplashSeen,
  SPLASH_FADE_MS,
} from "./components/SplashCurtain";
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import PlantDetailPage from "./pages/PlantDetailPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderConfirmationPage from "./pages/OrderConfirmationPage";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import AboutPage from "./pages/AboutPage";
import SellersPage from "./pages/SellersPage";
import OwnerPreviewPage from "./pages/OwnerPreviewPage";
import { PlantExpertDrawer } from "./components/PlantExpertDrawer";
import OwnerLoginPage from "./pages/OwnerLoginPage";
import OwnerLillyPage from "./pages/OwnerLillyPage";

function AppRouter() {
  const location = useLocation();
  const { publicMode, checked, envPublic } = usePublicMode();

  // Sync initial computation so there's no site flash before the splash mounts.
  const initialShow = envPublic && shouldShowSplash();
  const [splashMounted, setSplashMounted] = useState(initialShow);
  const [splashExiting, setSplashExiting] = useState(false);
  // Site starts invisible when splash is showing; fades in during crossfade.
  const [siteVisible, setSiteVisible] = useState(!initialShow);
  // Ribbon only starts its own animation after the crossfade has FULLY completed —
  // otherwise the ribbon's fade-in + hold + drift-out finishes while the homepage
  // is still fading in, and the sentence is gone by the time the site is visible.
  const [siteFullyIn, setSiteFullyIn] = useState(!initialShow);

  // If owner-preview check reveals owner, dismiss splash immediately.
  /*
  useEffect(() => {
    if (checked && !publicMode && splashMounted && !splashExiting) {
      dismissSplash();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, publicMode]);
  */
  // Flip siteFullyIn true once the crossfade completes.
  useEffect(() => {
    if (siteVisible && !siteFullyIn) {
      const t = setTimeout(() => setSiteFullyIn(true), SPLASH_FADE_MS + 60);
      return () => clearTimeout(t);
    }
  }, [siteVisible, siteFullyIn]);

  // Replay splash on every navigation to home (`/`) — never suppress.
  // Fires only when the pathname CHANGES to "/" from something else, so scroll,
  // hash changes, and staying on home don't retrigger it.
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    const prev = prevPathRef.current;
    const now = location.pathname;
    prevPathRef.current = now;
    if (envPublic && publicMode && now === "/" && prev !== "/") {
      setSplashMounted(true);
      setSplashExiting(false);
      setSiteVisible(false);
      setSiteFullyIn(false);
    }
  }, [location.pathname, envPublic, publicMode]);

  const dismissSplash = () => {
    if (splashExiting) return;
    markSplashSeen();
    setSplashExiting(true);
    setSiteVisible(true);
    setTimeout(() => setSplashMounted(false), SPLASH_FADE_MS + 30);
  };

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      {splashMounted && (
        <SplashCurtain
          dismissing={splashExiting}
          onDismiss={dismissSplash}
        />
      )}
      <div
        aria-hidden={splashMounted && !splashExiting}
        style={{
          opacity: siteVisible ? 1 : 0,
          transition: `opacity ${SPLASH_FADE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
        }}
      >
        <Header />
        <Routes>
          <Route path="/" element={<HomePage startRibbon={siteVisible} />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/shop/:slug" element={<PlantDetailPage />} />
          {/* Commerce routes: preserved but blocked when public mode is on */}
          <Route path="/checkout" element={publicMode ? <Navigate to="/" replace /> : <CheckoutPage />} />
          <Route path="/order/:orderId" element={publicMode ? <Navigate to="/" replace /> : <OrderConfirmationPage />} />
          <Route path="/auth" element={publicMode ? <Navigate to="/" replace /> : <AuthPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/sellers" element={<SellersPage />} />
          <Route path="/owner-preview" element={<OwnerPreviewPage />} />
          <Route path="/owner/login" element={<OwnerLoginPage />} />
          <Route path="/owner/lilly" element={<OwnerLillyPage />} />
        </Routes>
        <Footer />
        <CartDrawer />
        <GraceAssistant />
        <PlantExpertDrawer />
      </div>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <AppRouter />
            <Toaster position="bottom-right" richColors />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
